import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { identityServices } from '../identityServices'
import {
  RequestCurrentEmailVerificationCodePayload,
  RequestCurrentEmailVerificationCodeDTO,
} from '../identityTypes'
import { ApiErrorCode, ErrorDetail } from '@/api/apiTypes'
import { mapAxiosErrorToApiError } from '@/utils'

interface UseRequestCurrentEmailVerificationCodeOptions {
  onSuccess?: (data: RequestCurrentEmailVerificationCodeDTO) => void
  onError?: (error: { errorMessage?: string; code?: string }) => void
}

export const useRequestCurrentEmailVerificationCode = (
  options?: UseRequestCurrentEmailVerificationCodeOptions,
) => {
  const { t } = useTranslation()

  const mutation = useMutation<
    RequestCurrentEmailVerificationCodeDTO,
    ErrorDetail,
    RequestCurrentEmailVerificationCodePayload | void
  >({
    mutationFn: (data) =>
      identityServices.requestCurrentEmailVerificationCode(data || undefined),
    onSuccess: (data) => {
      options?.onSuccess?.(data)
    },
    onError: (err) => {
      const { code } = mapAxiosErrorToApiError(err)

      let errorMessage = t('api:errors.somethingWrong')

      if (code === ApiErrorCode.TOO_MANY_ATTEMPTS) {
        errorMessage = t('api:errors.manyAttempts')
      }

      options?.onError?.({
        errorMessage,
        code,
      })
    },
  })

  function requestCurrentEmailVerificationCode(
    payload?: RequestCurrentEmailVerificationCodePayload,
  ) {
    mutation.mutate(payload)
  }

  return {
    requestCurrentEmailVerificationCode,
    isLoading: mutation.isPending,
    ...mutation,
  }
}
